import { useEffect, useState } from 'react';

import { applyDocumentDirection, directionForLocale, type Direction } from './direction';

import { i18n } from './index';

/**
 * Keeps `<html lang>`/`<html dir>` in step with the active i18next language and returns the current
 * direction for Base UI's DirectionProvider: Base UI reads its own context, not `dir` from ancestor
 * DOM, so without this value menus and dropdowns keep LTR behavior on an RTL page.
 */
export function useDirection(): Direction {
  const [direction, setDirection] = useState<Direction>(() =>
    directionForLocale(i18n.language || 'en'),
  );

  useEffect(() => {
    // Runs on mount too: main.tsx sets direction before first paint, so this is idempotent
    // there, but it keeps the hook self-sufficient so the document can never drift from
    // DirectionProvider's value. Not `setDirection(...)` here — the lazy useState initializer
    // computed the mount value, so re-setting it would just force an extra render.
    applyDocumentDirection(i18n.language || 'en');

    const handleLanguageChanged = (locale: string) => {
      setDirection(applyDocumentDirection(locale));
    };
    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  return direction;
}
